import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import {
  EnergyCertificateContributionDto,
  EnergyCertificateResultPointDto,
  EnergyClassThresholdDto,
} from '@artemis-software/wr-api';

const CLASS_COLORS: Record<string, string> = {
  A: '#009640',
  B: '#52ae32',
  C: '#95c11f',
  D: '#ffed00',
  E: '#fbba00',
  F: '#ea5b0c',
  G: '#e2001a',
};

const CLASS_TEXT_COLORS: Record<string, string> = {
  A: '#ffffff',
  B: '#ffffff',
  C: '#1a1a1a',
  D: '#1a1a1a',
  E: '#1a1a1a',
  F: '#ffffff',
  G: '#ffffff',
};

const WARNING_LABELS: Record<string, string> = {
  MISSING_SENSOR: 'Sensor fehlt',
  NON_ENERGY_UNIT: 'Keine Energieeinheit',
  NO_DATA: 'Keine Daten im Zeitraum',
};

const FORECAST_HINT_WITHOUT_MEASUREMENTS = 'Prognosewert: Für diesen Sensor liegen noch keine Messdaten vor.';
const MONTHS_IN_PERIOD = 12;

function lastMonthOfPeriod(periodStart: string): Date {
  const [year, month] = periodStart.split('-').map(Number);
  return new Date(year, month - 1 + MONTHS_IN_PERIOD - 1, 1);
}

interface GaugeSegment {
  key: string;
  label: string;
  color: string;
  textColor: string;
  upperBound: number | null;
  active: boolean;
}

interface TrendMarker {
  x: number;
  y: number;
  point: EnergyCertificateResultPointDto;
}

@Component({
  standalone: false,
  selector: 'wr-energy-class-gauge',
  templateUrl: './energy-class-gauge.component.html',
  styleUrl: './energy-class-gauge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnergyClassGaugeComponent {
  readonly value = input<number | null>(null);
  readonly certifiedValue = input<number | null>(null);
  readonly energyClass = input<string | null>(null);
  readonly energyClasses = input<EnergyClassThresholdDto[]>([]);
  readonly periodStart = input<string | null>(null);
  readonly periodEnd = input<string | null>(null);
  readonly breakdown = input<EnergyCertificateContributionDto[]>([]);
  readonly trend = input<EnergyCertificateResultPointDto[]>([]);

  readonly insufficientData = computed(() => {
    const contributions = this.breakdown();
    return !contributions.length || contributions.every(c => c.warningReason !== 'NONE');
  });

  readonly displayValue = computed(() => (this.insufficientData() ? null : this.value()));

  readonly segments = computed<GaugeSegment[]>(() => {
    const active = this.insufficientData() ? null : this.energyClass();
    return this.energyClasses().map(c => ({
      key: c.energyClass,
      label: c.energyClass,
      color: CLASS_COLORS[c.energyClass] ?? '#999999',
      textColor: CLASS_TEXT_COLORS[c.energyClass] ?? '#ffffff',
      upperBound: c.upperBound ?? null,
      active: c.energyClass === active,
    }));
  });

  readonly classLabel = computed(() => {
    if (this.insufficientData()) return '–';
    return this.energyClass() ?? '–';
  });

  readonly classColor = computed(() => {
    if (this.insufficientData()) return '#999999';
    const c = this.energyClass();
    return c ? (CLASS_COLORS[c] ?? '#999999') : '#999999';
  });

  readonly classTextColor = computed(() => {
    if (this.insufficientData()) return '#ffffff';
    const c = this.energyClass();
    return c ? (CLASS_TEXT_COLORS[c] ?? '#ffffff') : '#ffffff';
  });

  readonly periodEndInclusive = computed(() => {
    const end = this.periodEnd();
    if (!end) return null;
    const date = new Date(end);
    date.setMonth(date.getMonth() - 1);
    return date;
  });

  readonly pointerPercent = computed<number | null>(() => {
    if (this.insufficientData()) return null;
    const segs = this.segments();
    const active = this.energyClass();
    if (!segs.length || !active) return null;
    const idx = segs.findIndex(s => s.key === active);
    if (idx < 0) return null;

    const segWidth = 100 / segs.length;
    let frac = 0.5;
    const v = this.value();
    if (v != null) {
      const lower = idx === 0 ? 0 : (segs[idx - 1].upperBound ?? 0);
      const upper = segs[idx].upperBound;
      if (upper != null && upper > lower) {
        frac = Math.min(1, Math.max(0, (v - lower) / (upper - lower)));
      }
    }
    return segWidth * idx + segWidth * frac;
  });

  private readonly normalizedTrend = computed<TrendMarker[]>(() => {
    const points = [...this.trend()].reverse();
    if (points.length < 2) return [];
    const values = points.map(p => p.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    return points.map((p, i) => ({
      x: (i / (points.length - 1)) * 100,
      y: 30 - ((p.value - min) / range) * 28 - 1,
      point: p,
    }));
  });

  readonly trendPoints = computed(() =>
    this.normalizedTrend()
      .map(m => `${m.x.toFixed(1)},${m.y.toFixed(1)}`)
      .join(' '),
  );

  readonly trendMarkers = computed(() => this.normalizedTrend());

  readonly trendStart = computed<EnergyCertificateResultPointDto | null>(() => this.normalizedTrend()[0]?.point ?? null);

  readonly trendEnd = computed<EnergyCertificateResultPointDto | null>(() => {
    const markers = this.normalizedTrend();
    return markers[markers.length - 1]?.point ?? null;
  });

  contributionLabel(contribution: EnergyCertificateContributionDto): string {
    if (contribution.label) return contribution.label;
    if (contribution.sensorName) {
      return contribution.sensorBlockName
        ? `${contribution.sensorName} (${contribution.sensorBlockName})`
        : contribution.sensorName;
    }
    return contribution.type === 'SENSOR' ? 'Sensor' : 'Absolutwert';
  }

  warningLabel(reason: string): string | null {
    return WARNING_LABELS[reason] ?? null;
  }

  forecastHint(contribution: EnergyCertificateContributionDto): string | null {
    if (!contribution.forecastUsed) return null;
    const { measuredSince, firstFullPeriodStart } = contribution;
    if (!measuredSince || !firstFullPeriodStart) return FORECAST_HINT_WITHOUT_MEASUREMENTS;
    const periodEnd = lastMonthOfPeriod(firstFullPeriodStart);
    return `Prognosewert: Der Sensor liefert erst seit ${formatDate(measuredSince, 'dd.MM.yyyy', 'de')} Messdaten. `
      + `Ab dem Zeitraum ${formatDate(firstFullPeriodStart, 'MM.yyyy', 'de')} – ${formatDate(periodEnd, 'MM.yyyy', 'de')} `
      + 'werden die gemessenen Werte verwendet.';
  }
}
